/**
 * Object Finder - Main Controller
 *
 * Finds optimal imaging targets by sky direction, time, and observing conditions.
 * This module coordinates all components of the object finder functionality.
 */

import AstroImagingCalculator from './astroCalculator.js';
import FastAstroSearch from './searchEngine.js';
import { PlotlyManager } from './plotlyManager.js';
import DirectionSelector from './directionSelector.js';
import LocationTimeSelector from './locationTimeSelector.js';
import { NightScheduleVisualization } from './visualizations/nightSchedule.js';
import { TimelineControls } from './components/timelineControls.js';
import SkyVisualization from './skyVisualization.js';
import SkyPreview from './skyPreview.js';
import { objectTypeLabel } from './displayNames.js';

export class ObjectFinder {
    constructor() {
        this.calculator = null;
        this.searchEngine = null;
        this.plotlyManager = null;
        this.directionSelector = null;
        this.locationTimeSelector = null;
        this.timelineVisualization = null;
        this.timelineControls = null;
        this.skyVisualization = null;
        this.skyPreview = null;
        this.isInitialized = false;
        this.currentResults = null;
        this.currentParams = null;
    }

    /**
     * Initialize the Object Finder
     * @returns {Promise<boolean>} Success status
     */
    async initialize({ onDataReload } = {}) {
        if (this.isInitialized) {
            return true;
        }

        try {
            console.log('Initializing Object Finder...');

            // Initialize core components
            this.calculator = new AstroImagingCalculator();
            this.searchEngine = new FastAstroSearch();
            this.plotlyManager = new PlotlyManager();
            this.directionSelector = new DirectionSelector();
            this.locationTimeSelector = new LocationTimeSelector();
            this.timelineVisualization = new NightScheduleVisualization('visibilityTimeline', {
                showRecommendations: false,
                astroCalculator: this.calculator
            });
            // Pass calculator to SkyVisualization for coordinate conversions
            this.skyVisualization = new SkyVisualization(this.calculator);
            this.skyPreview = new SkyPreview();

            // Initialize search engine
            await this.searchEngine.initialize();

            // Load data if needed
            await this.loadDataIfNeeded(onDataReload);

            // Initialize plotly manager
            this.plotlyManager.initialize();

            // Initialize direction selector
            this.directionSelector.initialize();

            // Initialize location time selector
            this.locationTimeSelector.initialize();

            // Initialize timeline visualization
            const timelineInitialized = await this.timelineVisualization.initialize();
            if (timelineInitialized) {
                this.timelineControls = new TimelineControls(this.timelineVisualization, {
                    enableURLState: false // Disable URL state for object finder
                });
                this.timelineControls.setupControls();
            }
            this.skyVisualization.initialize();
            this.skyPreview.initialize();

            // Validate calculations
            console.log('Validating astronomical calculations...');
            const validationResults = this.calculator.validateCalculations();
            if (!Object.values(validationResults).every(result => result === true)) {
                console.warn('Some calculations failed validation - results may be inaccurate');
            }

            this.isInitialized = true;
            console.log('Object Finder initialized successfully');
            return true;
        } catch (error) {
            console.error('Failed to initialize Object Finder:', error);
            return false;
        }
    }

    /**
     * Find objects in the specified sky direction
     * @param {Object} params - Search parameters
     * @returns {Promise<Array>} Array of found objects with visibility data
     */
    async findObjects(params) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        try {

            // Show loading indicator
            this.showLoading(true);

            // Validate parameters
            const validatedParams = this.validateParameters(params);
            if (!validatedParams.valid) {
                throw new Error(validatedParams.error);
            }

            // Store current parameters
            this.currentParams = validatedParams.params;

            // Step 1: Convert direction to RA/Dec for the given time/location
            const targetCoordinates = this.convertDirectionToCoordinates(
                validatedParams.params.azimuth,
                validatedParams.params.minAltitude,
                validatedParams.params.maxAltitude,
                validatedParams.params.latitude,
                validatedParams.params.longitude,
                validatedParams.params.observationDate
            );

            // Step 2: Search for objects in the direction cone
            const searchFilters = {
                minMagnitude: validatedParams.params.minMagnitude,
                maxMagnitude: validatedParams.params.maxMagnitude
            };

            const objectsInDirection = await this.searchObjectsInDirection(
                targetCoordinates,
                validatedParams.params.fieldOfView,
                searchFilters
            );

            console.log('Objects found in direction search:', objectsInDirection.length);
            console.log('Search parameters:', { targetCoordinates, fieldOfView: validatedParams.params.fieldOfView, searchFilters });

            // Step 3: Calculate visibility for each object
            const objectsWithVisibility = await this.calculateObjectVisibility(
                objectsInDirection,
                validatedParams.params
            );

            // Step 4: Rank objects by visibility duration and quality
            console.log('Before ranking:', objectsWithVisibility.length, 'objects with visibility');
            console.log('Sample object before ranking:', objectsWithVisibility[0]);

            const rankedObjects = this.rankObjects(objectsWithVisibility, validatedParams.params);
            console.log('After ranking:', rankedObjects.length, 'objects ranked');
            console.log('Sample ranked object:', rankedObjects[0]);

            // Step 5: Apply advanced filters
            const filteredObjects = this.applyAdvancedFilters(rankedObjects, validatedParams.params);
            console.log('After filtering:', filteredObjects.length, 'objects remaining');
            console.log('Sample filtered object:', filteredObjects[0]);

            // Store results
            this.currentResults = filteredObjects;

            console.log(`Found ${filteredObjects.length} objects in direction (${rankedObjects.length} before filtering)`);
            console.log('Final filtered objects sample:', filteredObjects.slice(0, 3));

            return filteredObjects;

        } catch (error) {
            console.error('Error finding objects:', error);
            this.showError(error.message);
            this.showLoading(false); // Hide loading only on error
            return [];
        }
    }

    /**
     * Validate search parameters
     * @param {Object} params - Parameters to validate
     * @returns {Object} Validation result
     */
    validateParameters(params) {
        const errors = [];

        // Check required parameters
        if (!params.latitude || isNaN(params.latitude)) {
            errors.push('Valid latitude is required');
        }
        if (!params.longitude || isNaN(params.longitude)) {
            errors.push('Valid longitude is required');
        }
        if (!params.observationDate) {
            errors.push('Observation date is required');
        }
        if (!params.azimuth || isNaN(params.azimuth)) {
            errors.push('Valid azimuth is required');
        }

        // Check parameter ranges
        if (params.latitude < -90 || params.latitude > 90) {
            errors.push('Latitude must be between -90 and 90 degrees');
        }
        if (params.longitude < -180 || params.longitude > 180) {
            errors.push('Longitude must be between -180 and 180 degrees');
        }
        if (params.azimuth < 0 || params.azimuth > 360) {
            errors.push('Azimuth must be between 0 and 360 degrees');
        }
        if (params.minAltitude < 0 || params.minAltitude > 90) {
            errors.push('Minimum altitude must be between 0 and 90 degrees');
        }
        if (params.maxAltitude < 0 || params.maxAltitude > 90) {
            errors.push('Maximum altitude must be between 0 and 90 degrees');
        }
        if (params.minAltitude >= params.maxAltitude) {
            errors.push('Minimum altitude must be less than maximum altitude');
        }

        if (errors.length > 0) {
            return { valid: false, error: errors.join('; ') };
        }

        return { valid: true, params };
    }

    /**
     * Convert sky direction to RA/Dec coordinates
     * @param {number} azimuth - Azimuth in degrees
     * @param {number} minAlt - Minimum altitude in degrees
     * @param {number} maxAlt - Maximum altitude in degrees
     * @param {number} lat - Observer latitude
     * @param {number} lon - Observer longitude
     * @param {string} date - Observation date
     * @returns {Object} Target coordinates and search area
     */
    convertDirectionToCoordinates(azimuth, minAlt, maxAlt, lat, lon, date) {
        // Use middle altitude for primary direction
        const targetAlt = (minAlt + maxAlt) / 2;

        // Convert to RA/Dec for the given time (use local midnight)
        const observationTime = new Date(date + 'T00:00:00');
        const coords = this.calculator.altAzToRaDec(targetAlt, azimuth, lat, lon, observationTime);

        console.log('Coordinate conversion result:', {
            input: { targetAlt, azimuth, lat, lon, date },
            output: coords
        });

        // Validate coordinate ranges
        if (isNaN(coords.ra) || isNaN(coords.dec)) {
            console.error('ERROR: Invalid coordinates generated!', coords);
        }
        if (coords.ra < 0 || coords.ra > 24) {
            console.warn('WARNING: RA out of range [0-24]:', coords.ra);
        }
        if (coords.dec < -90 || coords.dec > 90) {
            console.warn('WARNING: Dec out of range [-90,90]:', coords.dec);
        }

        return {
            centerRA: coords.ra,
            centerDec: coords.dec,
            azimuth: azimuth,
            minAltitude: minAlt,
            maxAltitude: maxAlt,
            observationTime: observationTime
        };
    }

    /**
     * Search for objects within the specified direction cone
     * @param {Object} coordinates - Target coordinates
     * @param {number} radius - Search radius in degrees
     * @param {Object} filters - Additional filters for magnitude, object type, etc.
     * @returns {Promise<Array>} Objects in direction
     */
    async searchObjectsInDirection(coordinates, radius, filters = {}) {
        console.log(`Searching for objects within ${radius}° of RA=${coordinates.centerRA.toFixed(3)}h, Dec=${coordinates.centerDec.toFixed(2)}°`);

        // Use the enhanced spatial search from FastAstroSearch
        const objectsInDirection = await this.searchEngine.searchByDirection(
            coordinates.centerRA,
            coordinates.centerDec,
            radius,
            filters
        );

        console.log(`Spatial search found ${objectsInDirection.length} objects in direction cone`);
        return objectsInDirection;
    }

    /**
     * Calculate visibility for each object
     * @param {Array} objects - Objects to analyze
     * @param {Object} params - Observation parameters
     * @returns {Promise<Array>} Objects with visibility data
     */
    async calculateObjectVisibility(objects, params) {
        const objectsWithVisibility = [];

        for (const obj of objects) {
            // Calculate visibility for the observation night
            const visibility = this.calculateNightVisibility(obj, params);

            console.log(`Visibility for ${obj.name || obj.designation}: ${visibility.totalDuration.toFixed(1)}h (min required: ${params.minDuration}h)`);

            if (visibility.totalDuration > params.minDuration) {
                objectsWithVisibility.push({
                    ...obj,
                    visibility: visibility
                });
            }
        }

        return objectsWithVisibility;
    }

    /**
     * Calculate visibility for a single night
     * @param {Object} object - Astronomical object
     * @param {Object} params - Observation parameters
     * @returns {Object} Visibility data
     */
    calculateNightVisibility(object, params) {
        const observationDate = new Date(params.observationDate);

        // Set up observation period: sunset to sunrise (extended to cover all twilight)
        const startTime = new Date(observationDate);
        startTime.setHours(18, 0, 0, 0); // Start at 6 PM local time

        // Calculate twilight times to extend visibility calculation through twilight
        const twilight = this.calculator.calculateTwilightTimes(
            observationDate,
            params.latitude,
            params.longitude
        );

        // End time: Default to 9 AM to cover all morning twilight
        const endTime = new Date(observationDate);
        endTime.setDate(endTime.getDate() + 1);
        endTime.setHours(9, 0, 0, 0); // End at 9 AM next day (covers morning twilight)

        // Extend if civil morning twilight goes past 9 AM
        if (twilight.morning && twilight.morning.civil) {
            const civilTime = new Date(observationDate);
            civilTime.setDate(civilTime.getDate() + 1);
            const civilParts = twilight.morning.civil.split(':');
            civilTime.setHours(parseInt(civilParts[0]), parseInt(civilParts[1]), 0, 0);

            if (civilTime > endTime) {
                endTime.setTime(civilTime.getTime() + 60 * 60 * 1000); // Add 1 hour buffer
            }
        }

        let totalVisibleDuration = 0;
        let moonFreeDuration = 0;
        let darkTimeOnly = 0;
        const visiblePeriods = [];
        const stepMinutes = 10; // Check every 10 minutes for better precision


        for (let time = new Date(startTime); time <= endTime; time.setMinutes(time.getMinutes() + stepMinutes)) {
            try {
                // Calculate object position
                const objAltAz = this.calculator.raDecToAltAz(
                    object.ra_hours,
                    object.dec_degrees,
                    params.latitude,
                    params.longitude,
                    time
                );

                // Check if object is above minimum altitude
                if (objAltAz.altitude >= params.minAltitude) {
                    // Check if within altitude range for the direction
                    const isInAltitudeRange = objAltAz.altitude >= params.minAltitude &&
                                            objAltAz.altitude <= params.maxAltitude;

                    // Check if roughly in the right azimuth direction (allow some tolerance)
                    const azimuthDiff = Math.abs(objAltAz.azimuth - params.azimuth);
                    const azimuthDiffNormalized = Math.min(azimuthDiff, 360 - azimuthDiff); // Handle wrap-around
                    const isInDirection = azimuthDiffNormalized <= params.fieldOfView;

                    if (isInAltitudeRange && isInDirection) {
                        totalVisibleDuration += stepMinutes / 60; // Convert to hours

                        // Check sun position for darkness
                        const sunAltitude = this.calculator.calculateSunAltitude(time, params.latitude, params.longitude);
                        const isDark = sunAltitude < -15; // Astronomical twilight
                        const isTwilight = sunAltitude >= -18 && sunAltitude < 0; // Any twilight (civil, nautical, astronomical)

                        if (isDark) {
                            darkTimeOnly += stepMinutes / 60;
                        }

                        // Check moon interference
                        let moonSeparation = null;
                        let moonAltitude = null;
                        let isMoonFree = true;

                        try {
                            moonAltitude = this.calculator.calculateMoonAltitude(time, params.latitude, params.longitude);

                            // Calculate moon separation from object
                            const moonAltAz = this.calculator.calculateMoonAltAz(time, params.latitude, params.longitude);
                            moonSeparation = this.calculator.calculateAngularDistance(
                                object.ra_hours,
                                object.dec_degrees,
                                moonAltAz.azimuth / 15, // Convert azimuth to hours approximation
                                moonAltitude
                            );

                            // Check if moon interferes
                            isMoonFree = moonAltitude < 0 || moonSeparation > params.moonAvoidance;

                            if (isMoonFree && isDark) {
                                moonFreeDuration += stepMinutes / 60;
                            }
                        } catch (moonError) {
                            console.warn('Moon calculation failed for time:', time, moonError);
                            // Assume moon-free if calculation fails
                            if (isDark) {
                                moonFreeDuration += stepMinutes / 60;
                            }
                        }

                        // Add to visible periods if it's dark OR during twilight (but sun is below horizon)
                        if (isDark || (isTwilight && sunAltitude < 0)) {
                            visiblePeriods.push({
                                time: new Date(time),
                                altitude: objAltAz.altitude,
                                azimuth: objAltAz.azimuth,
                                sunAltitude: sunAltitude,
                                moonAltitude: moonAltitude,
                                moonSeparation: moonSeparation,
                                isDark: isDark,
                                isMoonFree: isMoonFree
                            });
                        }
                    }
                }
            } catch (error) {
                console.warn('Visibility calculation failed for time:', time, error);
                continue;
            }
        }

        const result = {
            totalDuration: totalVisibleDuration,
            darkTimeDuration: darkTimeOnly,
            moonFreeDuration: moonFreeDuration,
            visiblePeriods: visiblePeriods,
            averageAltitude: visiblePeriods.length > 0 ?
                visiblePeriods.reduce((sum, p) => sum + p.altitude, 0) / visiblePeriods.length : 0,
            peakAltitude: visiblePeriods.length > 0 ?
                Math.max(...visiblePeriods.map(p => p.altitude)) : 0,
            riseTime: visiblePeriods.length > 0 ? visiblePeriods[0].time : null,
            setTime: visiblePeriods.length > 0 ? visiblePeriods[visiblePeriods.length - 1].time : null
        };


        return result;
    }

    /**
     * Rank objects by visibility and imaging suitability
     * @param {Array} objects - Objects with visibility data
     * @param {Object} params - Search parameters
     * @returns {Array} Ranked objects
     */
    rankObjects(objects, params) {
        console.log('rankObjects called with', objects.length, 'objects');

        // Calculate composite scores for sophisticated ranking
        const scoredObjects = objects.map((obj, index) => {
            const score = this.calculateObjectScore(obj, params, objects);
            if (index < 3) { // Log first 3 objects
                console.log(`Object ${index} (${obj.name}): score=${score.total}, visibility=${JSON.stringify(obj.visibility)}`);
            }
            return {
                ...obj,
                score: score,
                scoreBreakdown: score.breakdown
            };
        });

        // Sort by composite score (higher is better)
        const sorted = scoredObjects.sort((a, b) => b.score.total - a.score.total);
        console.log('rankObjects returning', sorted.length, 'scored objects');
        return sorted;
    }

    /**
     * Calculate comprehensive scoring for an object
     * @param {Object} object - Object with visibility data
     * @param {Object} params - Search parameters
     * @param {Array} allObjects - All objects for relative scoring
     * @returns {Object} Score object with breakdown
     */
    calculateObjectScore(object, params, allObjects) {
        const vis = object.visibility;
        const weights = {
            moonFreeDuration: 0.35,     // Most important: actual usable time
            darkTimeDuration: 0.25,     // Dark sky time
            averageAltitude: 0.15,      // Atmospheric seeing
            peakAltitude: 0.10,         // Best possible conditions
            objectType: 0.10,           // Imaging suitability by type
            magnitude: 0.05             // Brightness consideration
        };

        const scores = {};

        // 1. Moon-free duration score (0-100)
        const maxMoonFree = Math.max(...allObjects.map(o => o.visibility.moonFreeDuration));
        scores.moonFreeDuration = maxMoonFree > 0 ? (vis.moonFreeDuration / maxMoonFree) * 100 : 0;

        // 2. Dark time duration score (0-100)
        const maxDarkTime = Math.max(...allObjects.map(o => o.visibility.darkTimeDuration));
        scores.darkTimeDuration = maxDarkTime > 0 ? (vis.darkTimeDuration / maxDarkTime) * 100 : 0;

        // 3. Average altitude score (0-100) - exponential curve favoring higher altitudes
        scores.averageAltitude = Math.min(100, Math.pow(vis.averageAltitude / 90, 0.7) * 100);

        // 4. Peak altitude score (0-100) - bonus for excellent moments
        scores.peakAltitude = Math.min(100, Math.pow(vis.peakAltitude / 90, 0.7) * 100);

        // 5. Object type score (0-100) - imaging suitability
        scores.objectType = this.getObjectTypeScore(object);

        // 6. Magnitude score (0-100) - brightness consideration
        scores.magnitude = this.getMagnitudeScore(object, params);

        // Calculate weighted total
        const total = Object.keys(weights).reduce((sum, key) => {
            return sum + (scores[key] * weights[key]);
        }, 0);

        return {
            total: Math.round(total * 10) / 10,
            breakdown: {
                moonFreeDuration: Math.round(scores.moonFreeDuration * 10) / 10,
                darkTimeDuration: Math.round(scores.darkTimeDuration * 10) / 10,
                averageAltitude: Math.round(scores.averageAltitude * 10) / 10,
                peakAltitude: Math.round(scores.peakAltitude * 10) / 10,
                objectType: Math.round(scores.objectType * 10) / 10,
                magnitude: Math.round(scores.magnitude * 10) / 10
            },
            weights: weights
        };
    }

    /**
     * Get object type score for imaging suitability
     * @param {Object} object - Astronomical object
     * @returns {number} Score 0-100
     */
    getObjectTypeScore(object) {
        const type = objectTypeLabel(object).toLowerCase();
        const name = (object.name || object.designation || '').toLowerCase();

        // Deep sky objects score
        if (type.includes('galaxy') || name.includes('ngc') || name.includes('ic')) {
            return 95; // Excellent for deep-sky imaging
        }
        // HII regions and supernova remnants are emission nebulae; cometary globules are dark clouds
        if (type.includes('nebula') || type.includes('cluster') || type.includes('hii') || type.includes('supernova')
            || type.includes('globule')) {
            return 90; // Great for imaging
        }
        if (type.includes('star') && type.includes('double')) {
            return 75; // Good for high-resolution imaging
        }
        if (type.includes('star') && !type.includes('variable')) {
            return 60; // Stars less interesting for long exposure
        }
        if (type.includes('planet')) {
            return 85; // Good for planetary imaging
        }
        if (type.includes('asteroid') || type.includes('comet')) {
            return 70; // Moving targets require special technique
        }

        // Default for unknown types
        return 65;
    }

    /**
     * Get magnitude score
     * @param {Object} object - Astronomical object
     * @param {Object} params - Search parameters
     * @returns {number} Score 0-100
     */
    getMagnitudeScore(object, params) {
        const mag = object.magnitude;
        if (!mag || isNaN(mag)) return 50; // Neutral score for unknown magnitude

        const minMag = params.minMagnitude || -5;
        const maxMag = params.maxMagnitude || 15;

        // Linear scoring within range, with bonus for brighter objects
        if (mag < minMag) return 100; // Very bright
        if (mag > maxMag) return 0;   // Too faint

        // Score decreases as magnitude increases (fainter)
        const normalizedMag = (mag - minMag) / (maxMag - minMag);
        return Math.max(0, (1 - normalizedMag) * 100);
    }

    /**
     * Apply additional filters to ranked objects
     * @param {Array} objects - Ranked objects
     * @param {Object} params - Search parameters
     * @returns {Array} Filtered objects
     */
    applyAdvancedFilters(objects, params) {
        console.log('applyAdvancedFilters called with', objects.length, 'objects');
        console.log('Filter params:', params);

        let filtered = [...objects];
        console.log('Starting with', filtered.length, 'objects');

        // Filter by minimum visibility duration
        if (params.minDuration) {
            const beforeDuration = filtered.length;
            filtered = filtered.filter(obj =>
                obj.visibility.moonFreeDuration >= params.minDuration
            );
            console.log(`Duration filter (>=${params.minDuration}h): ${beforeDuration} → ${filtered.length} objects`);
        }

        // Filter by minimum altitude
        if (params.qualityThreshold) {
            const minAltitude = params.qualityThreshold === 'high' ? 45 :
                               params.qualityThreshold === 'medium' ? 30 : 15;
            const beforeAltitude = filtered.length;
            filtered = filtered.filter(obj =>
                obj.visibility.averageAltitude >= minAltitude
            );
            console.log(`Altitude filter (>=${minAltitude}°): ${beforeAltitude} → ${filtered.length} objects`);
        }

        // Filter by object types if specified
        if (params.objectTypes && params.objectTypes.length > 0) {
            const beforeTypes = filtered.length;
            filtered = filtered.filter(obj => {
                const type = objectTypeLabel(obj).toLowerCase();
                return params.objectTypes.some(filterType =>
                    type.includes(filterType.toLowerCase())
                );
            });
            console.log(`Object type filter: ${beforeTypes} → ${filtered.length} objects`);
        }

        // Limit results count
        const maxResults = params.maxResults || 50;
        const beforeLimit = filtered.length;
        const result = filtered.slice(0, maxResults);
        console.log(`Results limit (${maxResults}): ${beforeLimit} → ${result.length} objects`);

        return result;
    }

    /**
     * Show/hide loading indicator
     * @param {boolean} show - Whether to show loading
     */
    showLoading(show) {
        const loadingElement = document.getElementById('loading');
        if (loadingElement) {
            loadingElement.style.display = show ? 'block' : 'none';
        }
    }

    /**
     * Show error message
     * @param {string} message - Error message
     */
    showError(message) {
        const errorAlert = document.getElementById('errorAlert');
        const errorMessage = document.getElementById('errorMessage');

        if (errorAlert && errorMessage) {
            errorMessage.textContent = message;
            errorAlert.style.display = 'block';

            // Auto-hide after 5 seconds
            setTimeout(() => {
                errorAlert.style.display = 'none';
            }, 5000);
        }
    }

    /**
     * Get current search results
     * @returns {Array} Current results
     */
    getCurrentResults() {
        return this.currentResults;
    }

    /**
     * Get current search parameters
     * @returns {Object} Current parameters
     */
    getCurrentParams() {
        return this.currentParams;
    }

    /**
     * Generate sky visualization for current results
     * @param {Object} searchParams - Search parameters
     * @returns {Promise<boolean>} Success status
     */
    async generateSkyVisualization(searchParams) {
        if (!this.skyVisualization || !this.currentResults) {
            return false;
        }

        try {
            if (this.currentResults.length > 0) {
                console.log('Generating sky chart...');
                // Prepare search parameters for sky chart
                const skyParams = {
                    azimuth: searchParams.azimuth,
                    altitude: searchParams.altitude,
                    minAltitude: searchParams.minAltitude,
                    maxAltitude: searchParams.maxAltitude,
                    fieldOfView: searchParams.fieldOfView,
                    searchRadius: searchParams.searchRadius || 15,
                    latitude: searchParams.latitude,
                    longitude: searchParams.longitude,
                    observationDate: searchParams.observationDate
                };
                await this.skyVisualization.createSkyChart(this.currentResults, skyParams);
            } else {
                console.log('No objects found - showing empty sky chart');
                this.skyVisualization.showEmptySkyChart();
            }
            return true;
        } catch (error) {
            console.error('Error generating sky visualization:', error);
            return false;
        }
    }

    /**
     * Generate timeline visualization for current results
     * @param {Object} searchParams - Search parameters
     * @returns {Promise<boolean>} Success status
     */
    async generateTimelineVisualization(searchParams) {
        if (!this.timelineVisualization || !this.currentResults) {
            return false;
        }

        try {
            if (this.currentResults.length > 0) {
                console.log('Generating timeline visualization...');
                this.timelineVisualization.createTimeline(this.currentResults, searchParams);
            } else {
                console.log('No objects found - showing empty timeline');
                this.timelineVisualization.showEmptyTimeline();
            }
            return true;
        } catch (error) {
            console.error('Error generating timeline visualization:', error);
            // Clear timeline on error
            if (this.timelineVisualization) {
                this.timelineVisualization.clear();
            }
            return false;
        }
    }

    /**
     * Load the object database from the published JSON when the cache is empty or outdated
     * @param {Function} [onDataReload] - Called when a (slow) catalogue reload starts
     * @returns {Promise<boolean>} Success status
     */
    async loadDataIfNeeded(onDataReload) {
        try {
            await this.searchEngine.ensureDataLoaded({ onReloadStart: onDataReload });
            return true;
        } catch (error) {
            console.error('Failed to load data from JSON files:', error);
            return false;
        }
    }

    /**
     * Get ranking statistics for current results
     * @returns {Object} Ranking statistics
     */
    getRankingStatistics() {
        if (!this.currentResults || this.currentResults.length === 0) {
            return null;
        }

        const scores = this.currentResults.map(obj => obj.score.total);
        const durations = this.currentResults.map(obj => obj.visibility.moonFreeDuration);
        const altitudes = this.currentResults.map(obj => obj.visibility.averageAltitude);

        return {
            totalObjects: this.currentResults.length,
            scoreRange: {
                min: Math.min(...scores),
                max: Math.max(...scores),
                avg: scores.reduce((a, b) => a + b, 0) / scores.length
            },
            durationRange: {
                min: Math.min(...durations),
                max: Math.max(...durations),
                avg: durations.reduce((a, b) => a + b, 0) / durations.length
            },
            altitudeRange: {
                min: Math.min(...altitudes),
                max: Math.max(...altitudes),
                avg: altitudes.reduce((a, b) => a + b, 0) / altitudes.length
            }
        };
    }

    /**
     * Get explanation for object ranking
     * @param {Object} object - Ranked object
     * @returns {string} Ranking explanation
     */
    getObjectRankingExplanation(object) {
        if (!object.score) return "No ranking data available";

        const score = object.score;
        const vis = object.visibility;

        let explanation = `Score: ${score.total}/100\n`;
        explanation += `• Moon-free time: ${vis.moonFreeDuration.toFixed(1)}h (${score.breakdown.moonFreeDuration}/100)\n`;
        explanation += `• Dark time: ${vis.darkTimeDuration.toFixed(1)}h (${score.breakdown.darkTimeDuration}/100)\n`;
        explanation += `• Average altitude: ${vis.averageAltitude.toFixed(0)}° (${score.breakdown.averageAltitude}/100)\n`;
        explanation += `• Peak altitude: ${vis.peakAltitude.toFixed(0)}° (${score.breakdown.peakAltitude}/100)\n`;
        explanation += `• Object type suitability: ${score.breakdown.objectType}/100\n`;
        explanation += `• Brightness factor: ${score.breakdown.magnitude}/100`;

        return explanation;
    }

    /**
     * Filter objects by custom criteria
     * @param {Array} objects - Objects to filter
     * @param {Function} filterFn - Custom filter function
     * @returns {Array} Filtered objects
     */
    filterObjects(objects, filterFn) {
        if (!objects || !filterFn) return objects || [];
        return objects.filter(filterFn);
    }

    /**
     * Get objects by quality tier
     * @param {string} tier - 'excellent', 'good', 'fair', 'poor'
     * @returns {Array} Objects in the specified tier
     */
    getObjectsByQualityTier(tier = 'good') {
        if (!this.currentResults) return [];

        const thresholds = {
            excellent: 80,
            good: 65,
            fair: 50,
            poor: 0
        };

        const minScore = thresholds[tier];
        const maxScore = tier === 'excellent' ? 100 :
                       tier === 'good' ? thresholds.excellent :
                       tier === 'fair' ? thresholds.good : thresholds.fair;

        return this.currentResults.filter(obj =>
            obj.score.total >= minScore && obj.score.total < maxScore
        );
    }
}

export default ObjectFinder;